<template>
  <div class="dashboard-container p-6 space-y-8">
    <!-- Header Section -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Welcome back, {{ userDisplayName }}
        </h1>
        <p class="mt-2 text-gray-600 dark:text-gray-400">
          Your MCP Tools dashboard - manage and execute AI-powered tools
        </p>
      </div>
      <div class="mt-4 lg:mt-0 flex items-center space-x-4">
        <button
          :disabled="isRefreshing"
          class="btn-secondary flex items-center space-x-2"
          @click="refreshData"
        >
          <svg
            class="w-4 h-4"
            :class="{ 'animate-spin': isRefreshing }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>Refresh</span>
        </button>
        <RouterLink to="/tools" class="btn-primary"> Browse Tools </RouterLink>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg
              class="w-8 h-8 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Tools Executed</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ stats.toolsExecuted }}
            </p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg
              class="w-8 h-8 text-success-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Success Rate</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ stats.successRate }}%
            </p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg
              class="w-8 h-8 text-warning-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Response</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ stats.avgResponseTime }}ms
            </p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg
              class="w-8 h-8 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Storage Used</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ formatBytes(stats.storageUsed) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quota Usage -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Usage & Quotas</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Your current usage across different services
        </p>
      </div>
      <div class="card-body">
        <div class="space-y-6">
          <QuotaBar
            label="API Calls (Hourly)"
            :current="quotaUsage.apiCalls.currentHour"
            :limit="quotaUsage.apiCalls.limit"
            color="blue"
          />
          <QuotaBar
            label="Tool Executions (Hourly)"
            :current="quotaUsage.toolExecutions.currentHour"
            :limit="quotaUsage.toolExecutions.limit"
            color="primary"
          />
          <QuotaBar
            label="Vector Documents"
            :current="quotaUsage.vectorDocuments.current"
            :limit="quotaUsage.vectorDocuments.limit"
            color="purple"
          />
          <QuotaBar
            label="Storage"
            :current="quotaUsage.storage.currentBytes"
            :limit="quotaUsage.storage.limitBytes"
            :format-value="formatBytes"
            color="green"
          />
        </div>
      </div>
    </div>

    <!-- Two Column Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Recent Activity -->
      <div class="card">
        <div class="card-header">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h3>
            <RouterLink
              to="/tools?tab=history"
              class="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </RouterLink>
          </div>
        </div>
        <div class="card-body">
          <div v-if="recentExecutions.length === 0" class="text-center py-8">
            <svg
              class="w-12 h-12 text-gray-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p class="text-gray-500 dark:text-gray-400">No recent activity</p>
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="execution in recentExecutions"
              :key="execution.id"
              class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <div
                    class="w-2 h-2 rounded-full"
                    :class="{
                      'bg-success-500': execution.status === 'completed',
                      'bg-error-500': execution.status === 'failed',
                      'bg-warning-500': execution.status === 'pending',
                      'bg-blue-500': execution.status === 'running',
                    }"
                  />
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ formatToolName(execution.toolName) }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatTimeAgo(execution.startTime) }}
                  </p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ execution.duration ? `${execution.duration}ms` : '-' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Quick Actions</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Frequently used tools and shortcuts
          </p>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-2 gap-4">
            <QuickActionCard
              title="Web Search"
              description="Search the web with AI"
              icon="search"
              @click="openQuickTool('web_search')"
            />
            <QuickActionCard
              title="Calculator"
              description="Perform calculations"
              icon="calculator"
              @click="openQuickTool('calculate')"
            />
            <QuickActionCard
              title="Vector Store"
              description="Store & search docs"
              icon="document"
              @click="openQuickTool('vector_store')"
            />
            <QuickActionCard
              title="Telegram Bot"
              description="Send messages"
              icon="chat"
              @click="openQuickTool('telegram_bot')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Server Status -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Server Status</h3>
      </div>
      <div class="card-body">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center">
            <div class="flex items-center justify-center mb-2">
              <div
                class="w-3 h-3 rounded-full mr-2"
                :class="{
                  'bg-success-500': serverHealth.status === 'healthy',
                  'bg-warning-500': serverHealth.status === 'degraded',
                  'bg-error-500': serverHealth.status === 'unhealthy',
                }"
              />
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                {{ serverHealth.status }}
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400">System Status</p>
          </div>
          <div class="text-center">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ formatUptime(serverHealth.uptime) }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">Uptime</p>
          </div>
          <div class="text-center">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ availableTools.length }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">Available Tools</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { mcpApi } from '@/services/mcp-api';
import QuotaBar from '@/components/ui/QuotaBar.vue';
import QuickActionCard from '@/components/ui/QuickActionCard.vue';
import type { ToolExecution, McpTool } from '@/types/mcp';

const router = useRouter();
const authStore = useAuthStore();

// State
const isRefreshing = ref(false);
const recentExecutions = ref<ToolExecution[]>([]);
const availableTools = ref<McpTool[]>([]);
const serverHealth = ref({
  status: 'healthy' as 'healthy' | 'degraded' | 'unhealthy',
  uptime: 0,
  lastCheck: new Date().toISOString(),
});

// Stats
const stats = ref({
  toolsExecuted: 0,
  successRate: 0,
  avgResponseTime: 0,
  storageUsed: 0,
});

// Computed
const userDisplayName = computed(() => {
  const user = authStore.userProfile;
  if (!user) return 'User';

  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  return user.firstName || user.username || 'User';
});

const quotaUsage = computed(() => authStore.quotaUsage);

// Methods
const refreshData = async () => {
  if (isRefreshing.value) return;

  isRefreshing.value = true;

  try {
    await Promise.allSettled([
      loadRecentActivity(),
      loadServerHealth(),
      loadToolRegistry(),
      loadUserStats(),
      authStore.checkQuotaUsage(),
    ]);
  } catch (error) {
    console.error('Failed to refresh dashboard data:', error);
  } finally {
    isRefreshing.value = false;
  }
};

const loadRecentActivity = async () => {
  try {
    const executions = await mcpApi.getToolExecutionHistory(10);
    recentExecutions.value = executions;
  } catch (error) {
    console.error('Failed to load recent activity:', error);
  }
};

const loadServerHealth = async () => {
  try {
    const health = await mcpApi.getServerHealth();
    serverHealth.value = {
      status: health.status as any,
      uptime: health.uptime || 0,
      lastCheck: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Failed to load server health:', error);
  }
};

const loadToolRegistry = async () => {
  try {
    const registry = await mcpApi.getToolRegistry();
    availableTools.value = Object.values(registry.tools || {});
  } catch (error) {
    console.error('Failed to load tool registry:', error);
  }
};

const loadUserStats = async () => {
  try {
    const usage = await mcpApi.getUserToolUsage();

    // Calculate stats from usage data
    const totalExecutions = usage.reduce((sum, tool) => sum + tool.count, 0);
    const avgResponseTime = Math.round(Math.random() * 500 + 200); // Mock data
    const successRate = Math.round(95 + Math.random() * 5); // Mock data
    const storageUsed = Math.round(Math.random() * 50000000); // Mock data

    stats.value = {
      toolsExecuted: totalExecutions,
      successRate,
      avgResponseTime,
      storageUsed,
    };
  } catch (error) {
    console.error('Failed to load user stats:', error);
  }
};

const openQuickTool = (toolName: string) => {
  router.push({ name: 'tool-detail', params: { toolName } });
};

// Utility methods
const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

// Auto-refresh
let refreshInterval: number | null = null;

onMounted(async () => {
  await refreshData();

  // Auto-refresh every 30 seconds
  refreshInterval = window.setInterval(async () => {
    await loadRecentActivity();
    await loadServerHealth();
  }, 30000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});

// Set page title
document.title = 'Dashboard | MCP Tools Collection';
</script>

<style scoped>
.dashboard-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Animation for stats cards */
.card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-1px);
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
  }
}

/* Print styles */
@media print {
  .btn-secondary,
  .btn-primary {
    display: none;
  }
}
</style>
